import React, { createContext, useContext, ReactNode, useCallback, useMemo, useState } from 'react';

export type MenuItem = {
    id: string;
    textKey?: string;
    text?: string;
    link: string;
};

export type Menu = {
    id: string;
    textKey?: string;
    text?: string;
    items: MenuItem[];
};

export type Data = {
    menus: Menu[];
};

type TMenuContext = Readonly<Data> & {
    pushMenu(m: Menu, depth: number): void;
    popMenu(depth: number): void;
};

const MenuContext = createContext<TMenuContext>(undefined as any);

// The null menu is used as a placeholder in the menus
// array when menu items are created in the wrong order.
// (e.g. deepest menu is created before top-level menu)
const nullMenu: Menu = {
    id: '',
    items: [],
};

export type Props = {
    initialMenus?: Menu[];
    children?: ReactNode;
};

export const MenuProvider = ({ children, initialMenus = [] }: Props) => {
    const [ menus, setMenus ] = useState<Menu[]>(initialMenus);

    //
    // Add or update the given menu.
    // Note: I wanted this to work like a stack but the order of menu
    // creation depends on the order of rendering for components, which
    // depends on which components already exist in the hierarchy. So
    // we have a depth parameter to force the given menu to appear in
    // the correct order (e.g. the last menu could be created before
    // the top-level menus but we need to make sure it appears last)
    //
    const pushMenu = useCallback((menu: Menu, depth: number) => {
        setMenus(menus => {
            // Clone and pad out the menus array with placeholders, if necessary
            menus = [ ...menus ];
            while (menus.length < depth + 1) {
                menus.push(nullMenu);
            }
            // Overwrite the element at [depth]
            menus[depth] = menu;
            return menus;
        });
    }, [ setMenus ]);

    //
    // Remove the menu at the specified depth.
    //
    const popMenu = useCallback((depth: number) => {
        setMenus(menus => {
            // replace the menu with a null placeholder
            menus = menus.map((m, i) => i === depth ? nullMenu : m);

            // Strip trailing null menus from the array.
            // (placeholders are fine in the middle, but
            // they're unnecessary/unhelpful at the end)
            for (let i = menus.length - 1; i >= 0; i--) {
                if (menus[i] !== nullMenu) {
                    break;
                }
                menus.pop();
            }
            return menus;
        });
    }, [ setMenus ]);

    const value = useMemo(() => ({
        menus,
        pushMenu,
        popMenu,
    }), [ menus ]);

    return (
        <MenuContext.Provider value={ value }>
            { children }
        </MenuContext.Provider>
    );
};

export const useMenu = () => {
    return useContext(MenuContext);
};
